import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  WorkflowDto,
  WorkflowStepDto,
} from "@babackapp/private-api-client/types";
import { useParams } from "react-router-dom";
import { useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  EditWorkflowFormValues,
  WorkflowFormSchema,
} from "../workflow-form-fields";
import useEditWorkflow from "@babackapp/private-api-client/client/use-update-workflow";

type EditOneWorkflowProps = {
  currentWorkflow: WorkflowDto;
};

export const useWorkflowForm = ({ currentWorkflow }: EditOneWorkflowProps) => {
  const { shopId } = useParams<{ shopId: string }>();
  const [selectedStep, setSelectedStep] = useState<WorkflowStepDto>();

  const onSelectStep = (step: WorkflowStepDto) => setSelectedStep(step);

  const form = useForm<EditWorkflowFormValues>({
    resolver: zodResolver(WorkflowFormSchema),
    defaultValues: currentWorkflow,
  });

  const { mutateAsync: updateMutation } = useEditWorkflow(shopId || "");

  const deleteStep = useCallback(
    (stepId: string) => {
      const newflowStep = form
        .getValues()
        .steps.filter((step) => step.id != stepId);
      form.setValue("steps", newflowStep);
    },
    [form],
  );

  const addStep = useCallback(() => {
    const step: WorkflowStepDto = {
      id: uuidv4(),
      type: "ACTION",
      ressource: "returnRequest",
      operation: "returnRequest:returnRequest:create",
      parameters: {
        status: [],
        resolution: [],
        reason: [],
        users: [],
        customFields: [],
      },
    };
    form.setValue("steps", [...form.getValues("steps"), step]);
    return step;
  }, [form]);

  const deleteSelectedStep = useCallback(() => {
    if (!selectedStep) return;
    return deleteStep(selectedStep.id);
  }, [selectedStep]);

  const saveActionStep = useCallback(
    (step: WorkflowStepDto) => {
      const index = form.getValues("steps").findIndex((s) => s.id == step.id);
      if (index < 0) return;

      form.setValue("steps", [
        ...form.getValues("steps").slice(0, index),
        step,
        ...form.getValues("steps").slice(index + 1),
      ]);
    },
    [form],
  );

  const onSubmit = form.handleSubmit((data) => {
    updateMutation({
      body: {
        name: data.name,
        description: data.description,
        steps: data.steps,
        trigger: data.trigger,
        status: data.status,
      },
      workflowId: data.id,
    });
  });

  const saveTriggerStep = useCallback(
    (step: WorkflowStepDto) => {
      form.setValue("trigger", step);
      setSelectedStep(step);
    },
    [form, setSelectedStep],
  );

  const getFormValue = <K extends keyof EditWorkflowFormValues>(key: string) =>
    form.watch()[key as K];

  return {
    onSubmit,
    deleteStep,
    onSelectStep,
    setSelectedStep,
    selectedStep,
    saveTriggerStep,
    saveActionStep,
    deleteSelectedStep,
    addStep,
    getFormValue,
    form,
  };
};
